/**
 * Catalogue & disponibilité — the design's own screen, and the one that
 * answers "why does the customer see that".
 *
 * Both halves of every article side by side: the price their tariff applies
 * and the availability band their disclosure rule allows, beside the on-hand,
 * what this portal's own unshipped orders have committed, and what is
 * expected inbound. The two come from the sources the portal itself uses, so
 * the comparison is between one set of figures rather than two
 * implementations that agree most of the time.
 *
 * **The internal columns appear only with a depot chosen**, which is the
 * design's own toggle read the honest way round: there is nothing to be
 * internal *about* without a site, and asking for 32 000 articles' stock to
 * annotate twenty-four rows would be slow for no reason.
 *
 * `reserved` is on this screen and on no portal payload. Telling a customer
 * how much somebody else has ordered is not a disclosure rule anybody agreed.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { money, quantity, EM_DASH } from "@hardauto/core-i18n";
import {
  Badge,
  Checkbox,
  DataTable,
  Input,
  Page,
  Pagination,
  Select,
  Toolbar,
  type Column,
} from "@hardauto/core-ui";

import { CatalogApi, type AvailabilityRow } from "../api";
import { NAMESPACE } from "../i18n";
import "./catalog.css";

const PAGE = 50;

/** How each band is drawn. `unknown` is a real answer: no level at all. */
const BAND_TONE: Record<string, "success" | "warning" | "info" | "neutral"> = {
  available: "success",
  low: "warning",
  on_order: "info",
  unknown: "neutral",
};

export interface AvailabilityScreenProps {
  client: ApiClient;
  /**
   * The depots to choose from, injected by the application: sites belong to
   * the referential, and a feature may not import a sibling feature.
   */
  loadSites?: () => Promise<readonly { code: string; name: string }[]>;
  /** The tariffs to price under, injected for the same reason. */
  loadScales?: () => Promise<readonly { code: string; label: string }[]>;
}

export function AvailabilityScreen({ client, loadSites, loadScales }: AvailabilityScreenProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new CatalogApi(client), [client]);

  const [search, setSearch] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [scaleCode, setScaleCode] = useState("");
  const [publishedOnly, setPublishedOnly] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const term = useDebounced(search, 300);

  const sitesFetcher = useCallback(
    () => (loadSites ? loadSites() : Promise.resolve([])),
    [loadSites],
  );
  const sites = useResource(sitesFetcher);

  const scalesFetcher = useCallback(
    () => (loadScales ? loadScales() : Promise.resolve([])),
    [loadScales],
  );
  const scales = useResource(scalesFetcher);

  const fetcher = useCallback(
    () =>
      api.availability({
        search: term || undefined,
        siteCode: siteCode || undefined,
        scaleCode: scaleCode || undefined,
        publishedOnly: publishedOnly || undefined,
        afterId: cursor,
        limit: PAGE,
      }),
    [api, term, siteCode, scaleCode, publishedOnly, cursor],
  );
  const rows = useResource(fetcher);

  /** The desk's own columns, shown only once a depot is chosen. */
  const internal: Column<AvailabilityRow>[] = siteCode
    ? [
        {
          key: "real",
          header: t("availability.column.real"),
          align: "end",
          render: (row) => <Num value={row.real} locale={locale} />,
        },
        {
          key: "reserved",
          header: t("availability.column.reserved"),
          align: "end",
          render: (row) => <Num value={row.reserved} locale={locale} />,
        },
        {
          key: "forecast",
          header: t("availability.column.forecast"),
          align: "end",
          secondary: true,
          render: (row) => <Num value={row.forecast} locale={locale} />,
        },
        {
          key: "sellable",
          header: t("availability.column.sellable"),
          align: "end",
          render: (row) => (
            <span className="hardauto-num hardauto-num--strong">
              {row.sellable === null ? EM_DASH : quantity(row.sellable, locale)}
            </span>
          ),
        },
      ]
    : [];

  const columns: Column<AvailabilityRow>[] = [
    {
      key: "reference",
      header: t("availability.column.reference"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <code>{row.reference}</code>
          <span className="hardauto-cell__muted">{row.brand || EM_DASH}</span>
        </div>
      ),
    },
    {
      key: "designation",
      header: t("availability.column.designation"),
      render: (row) => row.designation,
    },
    {
      key: "unit",
      header: t("availability.column.unit"),
      secondary: true,
      render: (row) => row.sales_unit,
    },
    {
      key: "price",
      header: t("availability.column.price"),
      align: "end",
      render: (row) => (
        <div className="hardauto-cell__stack hardauto-cell__stack--end">
          <span className="hardauto-num hardauto-num--strong">{money(row.unit_price, locale)}</span>
          {/* The base price only when the tariff moved it: repeating the same
              number twice on every row says nothing. */}
          {row.unit_price !== row.base_price ? (
            <span className="hardauto-cell__muted">{money(row.base_price, locale)}</span>
          ) : null}
        </div>
      ),
    },
    ...internal,
    {
      key: "band",
      header: t("availability.column.customer"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Badge tone={BAND_TONE[row.band] ?? "neutral"}>
            {t(`availability.band.${row.band}`, { defaultValue: row.band })}
          </Badge>
          {/* The quantity a customer would see, when the article's rule
              allows one at all. Its absence here IS the answer to "why does
              the customer not see a number". */}
          {row.disclosed_quantity !== null ? (
            <span className="hardauto-cell__muted">
              {quantity(row.disclosed_quantity, locale)} {row.sales_unit}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      header: t("availability.column.status"),
      render: (row) => (
        <Badge tone={row.status === "published" ? "success" : "neutral"}>
          {/* The enrichment screen's own labels, because it is the same
              publication state and two translations of it would drift. */}
          {t(`fiches.status.${row.status}`, { defaultValue: row.status })}
        </Badge>
      ),
    },
    {
      key: "active",
      header: t("availability.column.active"),
      render: (row) => (
        <Badge tone={row.is_active ? "success" : "danger"}>
          {t(`availability.active.${row.is_active ? "active" : "inactive"}`)}
        </Badge>
      ),
    },
  ];

  return (
    <Page
      title={t("availability.title")}
      description={t("availability.description")}
      eyebrow={rows.data ? t("availability.count", { count: rows.data.total }) : undefined}
    >
      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("availability.searchLabel")}
          placeholder={t("availability.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setCursor(undefined);
          }}
        />
        <label className="hardauto-toolbar__field">
          <span className="hardauto-visually-hidden">{t("availability.site")}</span>
          <Select
            value={siteCode}
            onChange={(event) => {
              setSiteCode(event.target.value);
              setCursor(undefined);
            }}
          >
            <option value="">{t("availability.anySite")}</option>
            {(sites.data ?? []).map((site) => (
              <option key={site.code} value={site.code}>
                {site.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="hardauto-toolbar__field">
          <span className="hardauto-visually-hidden">{t("availability.scale")}</span>
          <Select
            value={scaleCode}
            onChange={(event) => {
              setScaleCode(event.target.value);
              setCursor(undefined);
            }}
          >
            <option value="">{t("availability.basePrice")}</option>
            {(scales.data ?? []).map((scale) => (
              <option key={scale.code} value={scale.code}>
                {scale.label}
              </option>
            ))}
          </Select>
        </label>
        <Checkbox
          label={t("availability.publishedOnly")}
          checked={publishedOnly}
          onChange={(event) => {
            setPublishedOnly(event.target.checked);
            setCursor(undefined);
          }}
        />
      </Toolbar>

      <p className="hardauto-catalog__note">
        {siteCode ? t("availability.noteWithSite") : t("availability.noteWithoutSite")}
      </p>

      <DataTable<AvailabilityRow>
        caption={t("availability.caption")}
        columns={columns}
        rows={rows.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={rows.loading}
        loadingLabel={t("availability.loading")}
        error={
          rows.failed
            ? {
                title: t("error.title"),
                onRetry: rows.reload,
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{
          title: t("availability.empty.title"),
          description: t("availability.empty.body"),
        }}
      />

      <Pagination
        nextLabel={t("availability.next")}
        previousLabel={t("availability.previous")}
        onNext={
          rows.data?.next_cursor ? () => setCursor(rows.data?.next_cursor ?? undefined) : undefined
        }
        onPrevious={cursor ? () => setCursor(undefined) : undefined}
        {...(rows.data ? { summary: t("availability.count", { count: rows.data.total }) } : {})}
        busy={rows.refreshing}
      />
    </Page>
  );
}

/** A quantity, or an em dash when the column does not apply. */
function Num({ value, locale }: { value: string | null; locale: string }) {
  return <span className="hardauto-num">{value === null ? EM_DASH : quantity(value, locale)}</span>;
}
